/**
 * Phone bottom bar for the back office (owners, managers, staff).
 *
 * Built from the same menu model as the sidebar (useNavModel), so a
 * construction business sees Projects and Materials, a shop sees Orders
 * and Products, and All businesses mode sees Group, Money and Reports.
 * Four tabs plus "Menu", which opens the full sidebar.
 */
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavModel, type NavItem } from "./Sidebar";

export default function AdminBottomNav({ onMenu }: { onMenu: () => void }) {
  const { navGroups, labelFor, isItemActive, showCashierNav, primaryPaths, chatUnread } = useNavModel();
  if (showCashierNav || navGroups.length === 0) return null;

  const items = navGroups.flatMap((g) => g.items).filter((it) => !it.locked);
  const byPath = new Map(items.map((it) => [it.path, it]));
  const tabs: NavItem[] = [];
  for (const p of primaryPaths) {
    const it = byPath.get(p);
    if (it && tabs.length < 4) tabs.push(it);
  }
  for (const it of items) {
    if (tabs.length >= 4) break;
    if (!tabs.includes(it)) tabs.push(it);
  }
  const menuActive = !tabs.some((t) => isItemActive(t.path));

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-sidebar-border bg-sidebar pb-[env(safe-area-inset-bottom,0px)] md:hidden"
      aria-label="Quick navigation"
    >
      <ul className="grid h-14" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((it) => {
          const active = isItemActive(it.path);
          const Icon = it.icon;
          const badge = it.badgeKey === "chatUnread" ? chatUnread : 0;
          return (
            <li key={it.path} className="min-w-0">
              <Link
                to={it.path}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex h-full flex-col items-center justify-center gap-0.5 px-1 text-[10px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                  active ? "text-white" : "text-sidebar-foreground/70",
                )}
              >
                <span className={cn("flex h-7 w-12 items-center justify-center rounded-full transition-colors", active && "bg-primary/25")}>
                  <Icon size={18} aria-hidden />
                </span>
                <span className="w-full truncate text-center">{labelFor(it)}</span>
                {badge > 0 && (
                  <span className="absolute right-[22%] top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-sidebar" aria-label={`${badge} unread`} />
                )}
              </Link>
            </li>
          );
        })}
        <li className="min-w-0">
          <button
            type="button"
            onClick={onMenu}
            className={cn(
              "flex h-full w-full flex-col items-center justify-center gap-0.5 px-1 text-[10px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
              menuActive ? "text-white" : "text-sidebar-foreground/70",
            )}
            aria-label="Open the full menu"
          >
            <span className={cn("flex h-7 w-12 items-center justify-center rounded-full", menuActive && "bg-primary/25")}>
              <Menu size={18} aria-hidden />
            </span>
            <span>Menu</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
